/**
 * AIVA-8663 — turns the Studio-configured AI notice (Platform Settings → Add-in
 * Configuration, delivered on the add-in settings payload) into the policy the
 * gate, storage and sheet follow.
 *
 * Anything missing or out of range falls back to the built-in notice: enabled,
 * {@link AI_NOTICE_COPY.body}, {@link AI_NOTICE_CONFIG.ttlDays} days — exactly
 * the AIVA-6639 behaviour, so an add-in talking to an older backend is unchanged.
 */
import type { AiNoticeSettings } from "../services/studio/types";
import { AI_NOTICE_CONFIG, MS_PER_DAY } from "./config";
import { AI_NOTICE_COPY, AI_NOTICE_VERSION } from "./content";

export interface AiNoticePolicy {
  /** False when the admin switched the notice off: the gate never shows. */
  readonly isEnabled: boolean;
  readonly text: string;
  readonly validityDays: number;
  /**
   * Part of the acknowledgement storage key. The built-in text keeps
   * {@link AI_NOTICE_VERSION} so existing acknowledgements stay valid; any other
   * text gets a version derived from the text, so editing it in Studio asks every
   * user to acknowledge again. AIVA-8663: switching the notice off and back on in
   * Studio adds a suffix derived from that moment, which asks everyone again too.
   */
  readonly version: string;
  readonly ttlMs: number;
}

// Mirrors the backend's OutlookIntegrationSetting.AiNoticeValidityDaysMin/Max.
const VALIDITY_DAYS_MIN = 1;
const VALIDITY_DAYS_MAX = 365;

/** FNV-1a (32-bit), hex. Not cryptographic — a stable fingerprint of a string. */
export function fnv1a(value: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

function resolveValidityDays(days: number | undefined): number {
  return typeof days === "number" && Number.isInteger(days) && days >= VALIDITY_DAYS_MIN && days <= VALIDITY_DAYS_MAX
    ? days
    : AI_NOTICE_CONFIG.ttlDays;
}

export function resolveAiNoticePolicy(settings: AiNoticeSettings | null | undefined): AiNoticePolicy {
  const configuredText = settings?.text?.trim();
  const text = configuredText || AI_NOTICE_COPY.body;
  const validityDays = resolveValidityDays(settings?.validityDays);
  const textVersion = text === AI_NOTICE_COPY.body ? AI_NOTICE_VERSION : `c${fnv1a(text)}`;
  const enabledAt = settings?.enabledAtUtc?.trim();
  return {
    isEnabled: settings?.isEnabled ?? AI_NOTICE_CONFIG.requireAcknowledgement,
    text,
    validityDays,
    version: enabledAt ? `${textVersion}.r${fnv1a(enabledAt)}` : textVersion,
    ttlMs: validityDays * MS_PER_DAY,
  };
}

/** The built-in policy, used whenever Studio supplies nothing. */
export const DEFAULT_AI_NOTICE_POLICY: AiNoticePolicy = resolveAiNoticePolicy(null);
